import { useMemo, useRef, useState, useEffect, useCallback, useLayoutEffect } from "react";
import { useNavigate } from "react-router-dom";
import { bildUrl } from "../api";
import { anordnen, BOX_B, BOX_H, type BaumDaten, type Ansicht } from "./baum-layout";
import { usePersonKarte } from "./PersonKarte";
import { umfeldKarte } from "./umfeld";

const farbe = (geschlecht: string, istWurzel: boolean) => {
  if (istWurzel) return { fuellung: "#fde68a", rand: "#b45309" };
  if (geschlecht === "m") return { fuellung: "#e0e7ff", rand: "#6366f1" };
  if (geschlecht === "w") return { fuellung: "#fce7f3", rand: "#db2777" };
  return { fuellung: "#e5e7eb", rand: "#6b7280" };
};

const kuerzen = (t: string, max: number) => (t.length > max ? t.slice(0, max - 1) + "…" : t);

interface Sicht {
  x: number;
  y: number;
  breite: number;
  hoehe: number;
}

export function Baum({
  daten,
  modus,
  umkreis,
}: {
  daten: BaumDaten;
  modus: Ansicht;
  /**
   * Mit welchem Umkreis geladen wurde. Nur fuer die Infokarte: wer am
   * Rand steht, dessen Familie ist nicht vollstaendig geladen, und dann
   * laesst die Karte die Zeile „Eltern … · 3 Kinder" weg, statt eine
   * halbe Familie zu behaupten.
   */
  umkreis?: number;
}) {
  const navigate = useNavigate();
  const svgRef = useRef<SVGSVGElement>(null);
  const { knoten, verbindungen, rahmen, wurzelKnoten } = useMemo(
    () => anordnen(daten, modus),
    [daten, modus],
  );

  /*
   * Eltern, Partner und Kinderzahl je Knoten - aus den Kanten, die in
   * derselben Antwort stecken. Genau die Angaben fehlen in einem Baum am
   * meisten, weil immer nur ein Ausschnitt gezeichnet ist.
   */
  const umfeld = useMemo(() => umfeldKarte(daten, umkreis), [daten, umkreis]);
  const namen = useMemo(() => {
    const m = new Map(daten.personen.map((p) => [p.id, `${p.vorname} ${p.famname}`.trim()]));
    return (id: number) => m.get(id) ?? null;
  }, [daten.personen]);
  const karte = usePersonKarte(namen);

  const [sicht, setSicht] = useState<Sicht | null>(null);
  const [zieht, setZieht] = useState<{ x: number; y: number; sicht: Sicht } | null>(null);

  /**
   * Startansicht: 1:1, also lesbare Schriftgröße, mittig auf der
   * Ausgangsperson. Alles einzupassen wäre bei einigen Dutzend Personen
   * eine Briefmarkensammlung — dafür gibt es die Schaltfläche „⤢".
   */
  const einpassen = useCallback(() => setSicht(rahmen), [rahmen]);

  const aufWurzel = useCallback(() => {
    const kasten = svgRef.current?.getBoundingClientRect();
    const breite = Math.min(kasten?.width || 1100, rahmen.breite);
    const hoehe = Math.min(kasten?.height || 620, rahmen.hoehe);
    const mx = wurzelKnoten?.x ?? rahmen.x + rahmen.breite / 2;
    const my = (wurzelKnoten?.y ?? rahmen.y + rahmen.hoehe / 2) + BOX_H / 2;
    setSicht({ x: mx - breite / 2, y: my - hoehe / 2, breite, hoehe });
  }, [rahmen, wurzelKnoten]);

  useLayoutEffect(() => {
    aufWurzel();
  }, [aufWurzel]);

  const zoomen = useCallback((faktor: number, zentrumX?: number, zentrumY?: number) => {
    setSicht((s) => {
      if (!s) return s;
      const zx = zentrumX ?? s.x + s.breite / 2;
      const zy = zentrumY ?? s.y + s.hoehe / 2;
      const neueBreite = Math.min(Math.max(s.breite * faktor, 300), 60000);
      const neueHoehe = (neueBreite / s.breite) * s.hoehe;
      return {
        x: zx - ((zx - s.x) * neueBreite) / s.breite,
        y: zy - ((zy - s.y) * neueHoehe) / s.hoehe,
        breite: neueBreite,
        hoehe: neueHoehe,
      };
    });
  }, []);

  useEffect(() => {
    const svg = svgRef.current;
    if (!svg) return;
    // Als eigener Zuhörer, weil React das Rad passiv anmeldet und
    // preventDefault dort nichts bewirkt — die Seite würde mitscrollen.
    const beiRad = (e: WheelEvent) => {
      e.preventDefault();
      const kasten = svg.getBoundingClientRect();
      setSicht((s) => {
        if (!s) return s;
        const zx = s.x + ((e.clientX - kasten.left) / kasten.width) * s.breite;
        const zy = s.y + ((e.clientY - kasten.top) / kasten.height) * s.hoehe;
        const faktor = e.deltaY > 0 ? 1.12 : 0.89;
        const neueBreite = Math.min(Math.max(s.breite * faktor, 300), 60000);
        const neueHoehe = (neueBreite / s.breite) * s.hoehe;
        return {
          x: zx - ((zx - s.x) * neueBreite) / s.breite,
          y: zy - ((zy - s.y) * neueHoehe) / s.hoehe,
          breite: neueBreite,
          hoehe: neueHoehe,
        };
      });
    };
    svg.addEventListener("wheel", beiRad, { passive: false });
    return () => svg.removeEventListener("wheel", beiRad);
  }, []);

  if (!sicht) {
    return <div className="h-[70vh] w-full rounded-xl border border-black/10 dark:border-white/10" />;
  }

  return (
    <div className="relative">
      <div className="absolute right-2 top-2 z-10 flex gap-1">
        {(
          [
            ["＋", () => zoomen(0.8), "näher"],
            ["－", () => zoomen(1.25), "weiter weg"],
            ["◉", aufWurzel, "auf die Ausgangsperson"],
            ["⤢", einpassen, "alles zeigen"],
          ] as const
        ).map(([zeichen, tun, titel]) => (
          <button
            key={zeichen}
            onClick={tun}
            title={titel}
            className="h-8 w-8 rounded-lg border border-black/15 bg-white/90 text-sm
                       hover:border-black/40 dark:border-white/15 dark:bg-neutral-800/90"
          >
            {zeichen}
          </button>
        ))}
      </div>

      <svg
        ref={svgRef}
        viewBox={`${sicht.x} ${sicht.y} ${sicht.breite} ${sicht.hoehe}`}
        className="h-[70vh] w-full touch-none rounded-xl border border-black/10 bg-white
                   dark:border-white/10 dark:bg-neutral-900"
        style={{ cursor: zieht ? "grabbing" : "grab" }}
        onPointerDown={(e) => {
          (e.target as Element).setPointerCapture?.(e.pointerId);
          setZieht({ x: e.clientX, y: e.clientY, sicht });
        }}
        onPointerMove={(e) => {
          if (!zieht) return;
          // Die Karte haengt an einer gemessenen Bildschirmstelle; beim
          // Schieben wandert der Knoten darunter weg.
          karte.schliessen();
          const kasten = svgRef.current!.getBoundingClientRect();
          setSicht({
            ...zieht.sicht,
            x: zieht.sicht.x - ((e.clientX - zieht.x) / kasten.width) * zieht.sicht.breite,
            y: zieht.sicht.y - ((e.clientY - zieht.y) / kasten.height) * zieht.sicht.hoehe,
          });
        }}
        onPointerUp={() => setZieht(null)}
        onPointerLeave={() => setZieht(null)}
      >
        <g
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          className="text-black/30 dark:text-white/35"
        >
          {verbindungen.map((v) => (
            <path key={v.familieId} d={v.d} />
          ))}
        </g>

        {knoten.map((k) => {
          const istWurzel = k.id === daten.wurzel;
          const f = farbe(k.person.geschlecht, istWurzel);
          const von = k.person.geburtVon?.slice(0, 4) ?? "";
          const bis = k.person.todVon?.slice(0, 4) ?? "";
          const lebensdaten = von || bis ? `${von}${bis ? "–" + bis : ""}` : "";
          const hatBild = !!k.person.portrait;

          return (
            <g
              key={k.id}
              transform={`translate(${k.x - BOX_B / 2} ${k.y})`}
              {...karte.fuer(k.person, {
                umfeld: umfeld.get(k.id),
                hinweis: "Klick: hierher wechseln · Doppelklick: Personenseite",
                beiKlick: () => navigate(`/baum/${k.id}`),
              })}
              onDoubleClick={() => navigate(`/person/${k.id}`)}
              className="cursor-pointer"
            >
              {/*
                Kein <title> mehr: der eingebaute Hinweis des Browsers kam
                nach gut einer Sekunde und legte sich ueber die Karte. Was
                dort stand, steht jetzt in ihr.
              */}
              <rect
                width={BOX_B}
                height={BOX_H}
                rx={8}
                fill={f.fuellung}
                stroke={f.rand}
                strokeWidth={istWurzel ? 2.5 : 1}
              />
              {hatBild && (
                <>
                  <clipPath id={`bild-${k.id}`}>
                    <circle cx={26} cy={BOX_H / 2} r={18} />
                  </clipPath>
                  <image
                    href={bildUrl(k.person.portrait!.medienId, 64, k.person.portrait!.tagId)}
                    x={8}
                    y={BOX_H / 2 - 18}
                    width={36}
                    height={36}
                    preserveAspectRatio="xMidYMid slice"
                    clipPath={`url(#bild-${k.id})`}
                  />
                  <circle cx={26} cy={BOX_H / 2} r={18} fill="none" stroke={f.rand} strokeWidth={1} />
                </>
              )}
              <text
                x={hatBild ? 52 : 12}
                y={24}
                fontSize={13}
                fontWeight={istWurzel ? 600 : 500}
                fill="#1f2937"
              >
                {kuerzen(k.person.vorname || "?", hatBild ? 11 : 15)}
              </text>
              <text x={hatBild ? 52 : 12} y={39} fontSize={12} fill="#374151">
                {kuerzen(k.person.famname || "", hatBild ? 11 : 15)}
              </text>
              {lebensdaten && (
                <text x={hatBild ? 52 : 12} y={52} fontSize={10} fill="#6b7280">
                  {lebensdaten}
                </text>
              )}
            </g>
          );
        })}
      </svg>

      {karte.karte}
    </div>
  );
}
